import { ArrowRight } from "lucide-react";
import type { FC, ReactNode } from "react";
import { Link } from "react-router-dom";

export type DistributionTabKey = "groups" | "contacts";

type DistributionTabsProps = {
  active: DistributionTabKey;
  /** タブと同じ行の右端に表示するアクション (新規作成ボタン等) */
  actions?: ReactNode;
};

const TABS: { key: DistributionTabKey; label: string; to: string }[] = [
  { key: "groups", label: "配信グループ", to: "/admin/distribution/groups" },
  { key: "contacts", label: "連絡先管理", to: "/admin/distribution/contacts" },
];

/**
 * 旧画面 → 新画面への対応リンクテーブル (ISSUE-803 Phase 1C)。
 * 旧 ID と新 ID は ID 保持戦略により同一なので、リンクが直接機能する。
 */
const NEW_PAGE_LINKS: Record<
  DistributionTabKey,
  { label: string; to: string }
> = {
  groups: { label: "新「企業グループ」画面", to: "/company-groups" },
  contacts: { label: "新「企業マスタ」画面", to: "/companies" },
};

/**
 * 配信先管理 (グループ / 連絡先) を行き来する下線タブ。
 * 右端に actions スロットを持ち、タブと同じ行に配置できる。
 * ISSUE-803 Phase 1C: 上部に新画面への移行案内バナーを追加 (旧画面は維持)。
 * @param {DistributionTabsProps} props 現在アクティブなタブ + 右端アクション
 * @return {JSX.Element} タブストリップ
 */
export const DistributionTabs: FC<DistributionTabsProps> = ({
  active,
  actions,
}) => {
  const newLink = NEW_PAGE_LINKS[active];
  return (
    <div className="mb-6">
      {/* 新画面への移行案内 (ISSUE-803 Phase 1C) */}
      <div className="mb-3 flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-violet-50 border border-violet-200 text-sm">
        <span className="text-violet-900">
          この画面はまもなく統合されます。
          <Link
            to={newLink.to}
            className="ml-2 inline-flex items-center gap-1 font-medium underline underline-offset-2 hover:no-underline"
          >
            {newLink.label}
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          への移行をお試しください。
        </span>
      </div>

      <div className="flex justify-between items-end gap-4 border-b border-slate-200">
        <div className="flex gap-6">
          {TABS.map(t => {
            const isActive = active === t.key;
            return (
              <Link
                key={t.key}
                to={t.to}
                className={`relative px-2 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-primary"
                    : "text-base-content/60 hover:text-base-content"
                }`}
              >
                {t.label}
                {isActive && (
                  <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-primary" />
                )}
              </Link>
            );
          })}
        </div>
        {actions && (
          <div className="flex gap-2 items-center pb-1">{actions}</div>
        )}
      </div>
    </div>
  );
};
